import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Lightweight app-wide undo/redo.
 *
 * Each completed destructive/state-change action calls `pushUndo({ label,
 * undo, redo })`. The provider keeps a stack (up to 20) of past actions
 * + a parallel redo stack. Cmd+Z (Ctrl+Z on Windows/Linux) pops from
 * undo, runs the undo handler, and pushes onto redo. Cmd+Shift+Z (or
 * Ctrl+Y / Ctrl+Shift+Z) does the reverse.
 *
 * A single toast surfaces the most-recent action with an inline Undo
 * button so the keyboard shortcut isn't the only path. Toast auto-
 * dismisses after 6s (Gmail pattern).
 *
 * The handlers are async because most actions hit the API; the provider
 * shows a spinner state during the round-trip and surfaces a brief error
 * if the handler throws.
 *
 * Skipped on purpose: rich-text editor (TipTap) and form text inputs —
 * those have native browser undo that's already what users want there.
 * The shortcut is suppressed when the target element is editable so we
 * don't fight the browser's input history. */

export interface UndoAction {
  /** Short verb phrase: "Mark task done", "Delete task". */
  label: string;
  undo: () => Promise<void> | void;
  /** Optional re-do; if absent, Cmd+Shift+Z is a no-op for that action. */
  redo?: () => Promise<void> | void;
}

interface UndoContextValue {
  pushUndo: (action: UndoAction) => void;
  undoCount: number;
  redoCount: number;
}

const UndoContext = createContext<UndoContextValue | null>(null);

export function useUndo(): UndoContextValue {
  const ctx = useContext(UndoContext);
  if (!ctx) throw new Error('useUndo must be inside <UndoProvider>');
  return ctx;
}

const MAX_STACK = 20;
const TOAST_TTL_MS = 6000;

export function UndoProvider({ children }: { children: React.ReactNode }) {
  const undoStackRef = useRef<UndoAction[]>([]);
  const redoStackRef = useRef<UndoAction[]>([]);
  const [toast, setToast] = useState<{ label: string; id: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dismissTimerRef = useRef<number | null>(null);

  const dismissToast = useCallback(() => {
    if (dismissTimerRef.current) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    setToast(null);
    setErr(null);
  }, []);

  const showToast = useCallback((label: string) => {
    if (dismissTimerRef.current) window.clearTimeout(dismissTimerRef.current);
    setErr(null);
    const id = Date.now() + Math.random();
    setToast({ label, id });
    dismissTimerRef.current = window.setTimeout(() => {
      setToast((t) => (t?.id === id ? null : t));
      dismissTimerRef.current = null;
    }, TOAST_TTL_MS);
  }, []);

  const pushUndo = useCallback(
    (action: UndoAction) => {
      undoStackRef.current.push(action);
      if (undoStackRef.current.length > MAX_STACK) undoStackRef.current.shift();
      // Pushing a new forward action invalidates the redo history (standard
      // editor behavior — branching off any other path discards the redo).
      redoStackRef.current = [];
      showToast(action.label);
    },
    [showToast],
  );

  const performUndo = useCallback(async () => {
    const action = undoStackRef.current.pop();
    if (!action) return;
    setBusy(true);
    setErr(null);
    try {
      await action.undo();
      redoStackRef.current.push(action);
      if (redoStackRef.current.length > MAX_STACK) redoStackRef.current.shift();
      showToast(`Undid: ${action.label}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Undo failed');
      // Restore the action so user can retry
      undoStackRef.current.push(action);
    } finally {
      setBusy(false);
    }
  }, [showToast]);

  const performRedo = useCallback(async () => {
    const action = redoStackRef.current.pop();
    if (!action?.redo) return;
    setBusy(true);
    setErr(null);
    try {
      await action.redo();
      undoStackRef.current.push(action);
      if (undoStackRef.current.length > MAX_STACK) undoStackRef.current.shift();
      showToast(`Redid: ${action.label}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Redo failed');
      redoStackRef.current.push(action);
    } finally {
      setBusy(false);
    }
  }, [showToast]);

  // Global keyboard shortcuts. Suppressed when the active element is an
  // editable surface (input / textarea / contentEditable) so the browser's
  // native input undo still works inside fields.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      // Skip when focus is inside a text-editing surface
      const target = e.target as HTMLElement | null;
      const inEditable =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);
      if (inEditable) return;

      const key = e.key.toLowerCase();
      // Redo: Cmd+Shift+Z or Ctrl+Y
      if ((key === 'z' && e.shiftKey) || (key === 'y' && !e.shiftKey)) {
        if (redoStackRef.current.some((a) => a.redo)) {
          e.preventDefault();
          performRedo();
        }
        return;
      }
      // Undo: Cmd+Z or Ctrl+Z (no shift)
      if (key === 'z' && !e.shiftKey) {
        if (undoStackRef.current.length > 0) {
          e.preventDefault();
          performUndo();
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [performUndo, performRedo]);

  // Re-render whenever the stacks change so the count exposed via context
  // is fresh. (Refs alone don't trigger re-renders.) We use a tick state
  // bumped any time a stack mutates.
  const [tick, setTick] = useState(0);
  const wrappedPush = useCallback(
    (action: UndoAction) => {
      pushUndo(action);
      setTick((t) => t + 1);
    },
    [pushUndo],
  );

  const value = useMemo<UndoContextValue>(
    () => ({
      pushUndo: wrappedPush,
      undoCount: undoStackRef.current.length,
      redoCount: redoStackRef.current.filter((a) => a.redo).length,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wrappedPush, tick],
  );

  return (
    <UndoContext.Provider value={value}>
      {children}
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed inset-x-0 bottom-16 z-50 flex justify-center px-4"
        >
          <div className="pointer-events-auto flex items-center gap-3 rounded-full border border-border bg-popover/95 px-4 py-2 text-sm text-popover-foreground shadow-xl backdrop-blur">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
              {err ? 'Error' : busy ? 'Working…' : ''}
            </span>
            <span className="truncate">{err ?? toast.label}</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  performUndo().then(() => {
                    setTick((t) => t + 1);
                  });
                }}
                disabled={busy || undoStackRef.current.length === 0}
                className="rounded-full bg-primary px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-30"
              >
                Undo
              </button>
              <button
                type="button"
                onClick={dismissToast}
                aria-label="Dismiss"
                className="grid size-6 place-items-center rounded-full text-muted-foreground/70 transition-colors hover:bg-accent hover:text-foreground"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </UndoContext.Provider>
  );
}
